/**
 * DraggableWidthWrapper
 * ---------------------
 * A React component that enables horizontal resizing (drag-to-resize) for a target element.
 * It uses Pointer Events with pointer capture—no global window listeners required.
 *
 * Features:
 * - Supports both LTR and RTL layouts
 * - Enforces min and max width limits
 * - Can write the new width directly to a target DOM element or CSS variable
 * - Includes an optional "fallback" mode to temporarily lock width to a default value
 * - Smoothly updates DOM width using requestAnimationFrame for better performance
 *
 * Usage:
 * ------
 * Wrap this component around content that should be resizable, and specify the element
 * whose width should be controlled using the `targetElement` prop (a DOM element or a selector string).
 *
 * Example:
 * <DraggableWidthWrapper targetElement=".dock-panel" min={400} max={800} onDragEnd={handleResize}>
 *   <YourPanelContent />
 * </DraggableWidthWrapper>
 *
 * Props:
 * - dir: "ltr" | "rtl" (default: "ltr") – determines which edge is draggable
 * - min, max, initial: numeric width boundaries (in px)
 * - targetElement: DOM node or CSS selector of the element to resize
 * - updateCSSVar: if true, sets the width to a CSS variable instead of inline width
 * - cssVar: name of the CSS variable to update (e.g., "--panel-width")
 * - temporarilyFallBackToInitialWidth: when true, locks width to `fallbackWidth` until disabled
 * - fallbackWidth: width (in px) to use while fallback mode is active (default: 380)
 *
 * Callbacks:
 * - onDragMove?: (width: number, event: PointerEvent | TouchEvent | MouseEvent) => void
 *      Fired during dragging once per animation frame, **after** the DOM width is written.
 *      Coalesced with `requestAnimationFrame` so it won’t spam on every raw pointer event.
 *      Receives the latest computed width and the most recent pointer event.
 *      Not called while `temporarilyFallBackToInitialWidth` is true.
 *
 * - onDragEnd?: (finalWidth: number, event: PointerEvent | TouchEvent | MouseEvent) => void
 *      Fired when the user releases/cancels the drag. Receives the final committed width.
 */

import React, { useRef, useEffect, useReducer, useCallback } from "react";

const ACTIONS = {
	INIT: "INIT",
	START_DRAG: "START_DRAG",
	END_DRAG: "END_DRAG",
	SET_COMMITTED: "SET_COMMITTED",
	SET_FALLBACK: "SET_FALLBACK"
};

function reducer(state, action) {
	switch (action.type) {
		case ACTIONS.INIT:
			return { ...state, committedWidth: action.width };
		case ACTIONS.START_DRAG:
			return { ...state, dragging: true, pointerId: action.pointerId };
		case ACTIONS.END_DRAG:
			return { ...state, dragging: false, committedWidth: action.width, pointerId: null };
		case ACTIONS.SET_COMMITTED:
			return { ...state, committedWidth: action.width };
		case ACTIONS.SET_FALLBACK:
			return { ...state, fallbackActive: action.active };
		default:
			return state;
	}
}

const getTarget = targetElement => {
	if (targetElement) {
		if (typeof targetElement === "string") {
			const selector = targetElement.startsWith(".") ? targetElement : `.${targetElement}`;
			return document.querySelector(selector);
		}
		return targetElement;
	}
};

const DraggableWidthWrapper = ({
	dir = "ltr",
	min = 380,
	max = 900,
	initial = 380,
	children,
	dragEnabled = true,
	temporarilyFallBackToInitialWidth = false,
	targetElement,
	useTargetElementForWidthCalculation = false,
	onDragEnd,
	onDragMove,
	updateCSSVar = false,
	cssVar,
	fallbackWidth = 380,
	handleOffset = 0,
	handleWidth = 8,
	containSize = true
}) => {
	const containerRef = useRef(null);
	const handleRef = useRef(null);
	const rafRef = useRef(null);
	const liveWidthRef = useRef(initial);
	const lastEventRef = useRef(null);

	const [state, dispatch] = useReducer(reducer, {
		dragging: false,
		committedWidth: initial,
		fallbackActive: false,
		pointerId: null
	});

	const writeWidth = useCallback(
		w => {
			const el = getTarget(targetElement);
			if (!el?.style) return;
			el.style.width = `${w}px`;
			if (updateCSSVar && cssVar) el.style.setProperty(cssVar, `${w}px`);
		},
		[targetElement, updateCSSVar, cssVar]
	);

	// Coalesce DOM writes + onDragMove notifications into the same rAF
	const scheduleWidthWrite = useCallback(
		(w, evt) => {
			liveWidthRef.current = w;
			if (evt) lastEventRef.current = evt;
			if (rafRef.current) return;
			rafRef.current = requestAnimationFrame(() => {
				writeWidth(liveWidthRef.current);
				// Notify live change after DOM write, once per frame
				if (onDragMove) onDragMove(liveWidthRef.current, lastEventRef.current);
				rafRef.current = null;
			});
		},
		[writeWidth, onDragMove]
	);

	// Apply initial width once target exists
	useEffect(() => {
		const clamped = Math.min(Math.max(initial, min), max);

		let observer;
		const tryInitOnce = () => {
			const el = getTarget(targetElement);
			if (el) {
				liveWidthRef.current = clamped;
				writeWidth(clamped);
				dispatch({ type: ACTIONS.INIT, width: clamped });
				return true;
			}
			return false;
		};

		if (tryInitOnce()) return;

		observer = new MutationObserver(() => {
			if (tryInitOnce() && observer) {
				observer.disconnect();
			}
		});

		observer.observe(document.body, { childList: true, subtree: true });

		return () => observer?.disconnect();
	}, [initial, min, max, writeWidth, targetElement]);

	// Temporary fallback toggle
	useEffect(() => {
		dispatch({ type: ACTIONS.SET_FALLBACK, active: temporarilyFallBackToInitialWidth });
		if (temporarilyFallBackToInitialWidth) {
			writeWidth(fallbackWidth);
		} else {
			writeWidth(liveWidthRef.current);
		}
	}, [temporarilyFallBackToInitialWidth, fallbackWidth, writeWidth]);

	// Pointer math
	const computeWidthFromEvent = useCallback(
		e => {
			const sourceElement = useTargetElementForWidthCalculation ? getTarget(targetElement) : containerRef.current;
			if (!sourceElement) return liveWidthRef.current;
			const point = e.touches ? e.touches[0] : e;
			const rect = sourceElement.getBoundingClientRect();
			const raw = dir === "rtl" ? rect.right - point.clientX : point.clientX - rect.left;
			const clamped = Math.min(Math.max(raw, min), max);
			return clamped;
		},
		[dir, min, max, targetElement, useTargetElementForWidthCalculation]
	);

	// Start drag on the handle only; capture the pointer
	const onPointerDown = useCallback(
		e => {
			if (!dragEnabled || state.fallbackActive) return;
			// Ensure this is left button or a touch/pen
			if (e.button != null && e.button !== 0) return;
			handleRef.current?.setPointerCapture(e.pointerId);
			dispatch({ type: ACTIONS.START_DRAG, pointerId: e.pointerId });
			e.preventDefault?.(); // avoid text selection
		},
		[dragEnabled, state.fallbackActive]
	);

	// Move events are routed to the handle because of pointer capture
	const onPointerMove = useCallback(
		e => {
			if (!state.dragging) return;
			if (state.pointerId != null && e.pointerId !== state.pointerId) return;
			// Prevent scroll while dragging (especially on touch)
			if (e.cancelable) e.preventDefault();
			const nextWidth = computeWidthFromEvent(e);
			scheduleWidthWrite(nextWidth, e); // pass event so onDragMove can receive it
		},
		[state.dragging, state.pointerId, computeWidthFromEvent, scheduleWidthWrite]
	);

	// End/cancel on the handle; release capture
	const endDrag = useCallback(
		e => {
			if (!state.dragging) return;
			if (state.pointerId != null && e.pointerId !== state.pointerId) return;
			try {
				handleRef.current?.releasePointerCapture(e.pointerId);
			} catch {}
			const finalWidth = liveWidthRef.current;
			dispatch({ type: ACTIONS.END_DRAG, width: finalWidth });
			onDragEnd?.(finalWidth, e);
		},
		[state.dragging, state.pointerId, onDragEnd]
	);

	const edgeStyle = dir === "rtl" ? { left: handleOffset } : { right: handleOffset };

	return (
		<div
			ref={containerRef}
			style={{
				position: "relative",
				height: "100%",
				width: "100%",
				contain: containSize ? "layout size" : "layout",
				willChange: "width"
			}}
			data-width={state.committedWidth}
		>
			<div style={{ height: "100%" }}>{children}</div>

			<div
				ref={handleRef}
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={endDrag}
				onPointerCancel={endDrag}
				aria-disabled={!dragEnabled || state.fallbackActive}
				style={{
					position: "absolute",
					top: 0,
					height: "100%",
					width: handleWidth,
					...edgeStyle,
					zIndex: 10,
					background: "transparent",
					cursor: dragEnabled && !state.fallbackActive ? "col-resize" : "default",
					pointerEvents: dragEnabled && !state.fallbackActive ? "auto" : "none",
					// Crucial for touch devices: disables browser gestures so dragging feels immediate
					touchAction: "none"
				}} data-testid="orion-components-shared-components-draggable-width-wrapper-draggable-width-wrapper-div"
			/>
		</div>
	);
};

export default DraggableWidthWrapper;
